import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Pencil, Plus, Settings } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents, formatDimensions } from '../../lib/format';
import { SPACE_TYPE_LABELS } from '../../lib/fit';
import { StatusPill } from '../../components/admin/unitStatus';
import EditUnitDialog from '../../components/admin/EditUnitDialog';

export default function UnitsPage() {
  const { facilityId } = useParams();
  const [facility, setFacility] = useState(null);
  const [units, setUnits] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setState('loading');
    try {
      const payload = await api.adminUnits(facilityId);
      setFacility(payload.facility);
      setUnits(payload.units);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { load(); }, [facilityId]);

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }
  if (state === 'error') {
    return <p className="mx-auto mt-8 max-w-md rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <Link to="/admin" className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Operations
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{facility?.name}</h1>
          <p className="mt-1 text-sm text-slate-600">
            {units.length} units. Changing a rate applies to the <strong>next</strong> lease — a sitting
            tenant keeps the rate they signed at.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/admin/facilities/${facilityId}/edit`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
            <Settings className="h-4 w-4" aria-hidden="true" /> Facility settings
          </Link>
          <Link to={`/admin/facilities/${facilityId}/units/new`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-bold text-white hover:bg-brand-700">
            <Plus className="h-4 w-4" aria-hidden="true" /> Add units
          </Link>
        </div>
      </div>

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[52rem] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Unit</th>
              <th className="px-4 py-2.5">Size</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Tenant</th>
              <th className="px-4 py-2.5 text-right">Street rate</th>
              <th className="px-4 py-2.5 text-right">Leased at</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {units.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">
                  This facility has no units yet.{' '}
                  <Link to={`/admin/facilities/${facilityId}/units/new`} className="font-bold text-brand-700 hover:underline">
                    Add the first row
                  </Link>.
                </td>
              </tr>
            )}
            {units.map((u) => (
              <tr key={u.public_id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-bold text-slate-900">{u.unit_number}</p>
                  <p className="text-xs text-slate-500">{SPACE_TYPE_LABELS[u.space_type] ?? u.space_type}</p>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {formatDimensions(u.length_ft, u.width_ft)}
                  {u.is_pull_through && <span className="block text-slate-400">pull-through</span>}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={u.status} />
                </td>
                <td className="px-4 py-3 text-xs">
                  {u.lease_number ? (
                    <>
                      <span className="font-semibold text-slate-800">{u.first_name} {u.last_name}</span>
                      <span className="block text-slate-500">{u.lease_number}</span>
                    </>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">
                  {formatCents(u.monthly_rate_cents)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {u.leased_rate_cents == null ? <span className="text-slate-400">—</span> : (
                    <>
                      <span className="font-semibold text-slate-900">{formatCents(u.leased_rate_cents)}</span>
                      {u.rate_delta_cents !== 0 && (
                        <span className={`block text-[12px] font-bold ${u.rate_delta_cents < 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                          {u.rate_delta_cents < 0 ? '' : '+'}{formatCents(u.rate_delta_cents)} vs street
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button type="button" onClick={() => setEditing(u)} aria-label={`Edit unit ${u.unit_number}`}
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditUnitDialog unit={editing} facilityId={facilityId}
                        onClose={() => setEditing(null)}
                        onDone={() => { setEditing(null); load(); }}
                        onError={setError} />
      )}
    </div>
  );
}
